import { gql, NetworkStatus, type TypedDocumentNode } from '@apollo/client'
import { QueryRef, useLazyQuery, useReadQuery } from '@apollo/client/react'
import { pdf } from '@react-pdf/renderer'
import { ErrorAlert } from '@repo/shared-client/components/blocks'
import { CheckBoxFormField } from '@repo/shared-client/components/fields'
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Spinner,
} from '@repo/shared-client/components/ui'
import { Form, useForm } from '@repo/shared-client/form'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import SFBackPageHeader from '@web/src/components/primatives/sf-back-page-header/sf-back-page-header'
import SFPageLoading from '@web/src/components/primatives/sf-page-loading/sf-page-loading'
import EventAttendeesPdfDocument from '@web/src/features/event/event-attendees-pdf-document/event-attendees-pdf-document'
import { queryAndPreload } from '@web/src/lib/apollo/query-and-preload'
import { env } from '@web/src/lib/env.client'
import { EventInfo, ProfileBasicInfo, ProfileMediaInfo } from '@web/src/lib/fragments'
import { useTranslation } from '@web/src/lib/intl/react'
import { robotsMetaForEnvironment, seo } from '@web/src/lib/seo'
import dayjs from 'dayjs'
import { CircleAlertIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import type {
  EventAttendanceFilterStatus,
  EventAttendanceStatus,
  EventAttendeeListPageQuery,
  EventAttendeeListPageQueryVariables,
  EventAttendeesPdfExportQuery,
  EventAttendeesPdfExportQueryVariables,
} from 'types/graphql'

const SLUG_SUFFIX_REGEX = /-[a-zA-Z0-9]{6}$/

const EVENT_ATTENDEES_PDF_EXPORT_QUERY: TypedDocumentNode<
  EventAttendeesPdfExportQuery,
  EventAttendeesPdfExportQueryVariables
> = gql`
  query EventAttendeesPdfExportQuery($eventIdOrSlug: String!) {
    event: event(eventIdOrSlug: $eventIdOrSlug) {
      ...EventInfo
    }
  }
  ${EventInfo}

`

export const ATTENDEES_QUERY: TypedDocumentNode<EventAttendeeListPageQuery, EventAttendeeListPageQueryVariables> = gql`
    query EventAttendeeListPageQuery($page: Int, $filter: EventAttendeeFilter, $sortBy: EventAttendeeSortEnum) {
      eventAttendeeListPage(page: $page, filter: $filter, sortBy: $sortBy) {
        entries {
          id
          profile {
            ...ProfileBasicInfo
          }
          notes
          paymentStatus
          paymentAmount
          peopleQty
          status
        }
      }
    }
    ${ProfileBasicInfo}
    ${ProfileMediaInfo}
  `

export const Route = createFileRoute('/(auth)/(main-layout)/event/$eventIdOrSlug_/pdf-export')({
  component: EventAttendeesPdfExportPage,
  head: () =>
    seo({
      title: `Event PDF Export | ${env.VITE_APP_NAME}`,
      description: 'Export Event PDF Information',
      robots: robotsMetaForEnvironment({ all: 'noindex,nofollow' }),
    }),
  loader: async ({ context, params }) => {
    const { queryRef } = await queryAndPreload(context, EVENT_ATTENDEES_PDF_EXPORT_QUERY, {
      variables: { eventIdOrSlug: params.eventIdOrSlug },
    })
    return { queryRef }
  },
})

function EventAttendeesPdfExportPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { queryRef } = Route.useLoaderData()
  const { data, error, networkStatus } = useReadQuery(queryRef as QueryRef<EventAttendeesPdfExportQuery>)
  const isInitialLoading = networkStatus === NetworkStatus.loading

  const [isGenerating, setIsGenerating] = useState(false)
  const [pdfGenerated, setPdfGenerated] = useState(false)

  const event = data?.event
  const hasAdmission = Boolean(event?.admission)

  const formMethods = useForm({
    defaultValues: {
      includeAttendeeNote: true,
      includePaymentAmounts: hasAdmission,
      includePaymentStatus: hasAdmission,
      includeStatus: true,
      includeQuantity: true,
      statusGOING: true,
      statusNOTGOING: false,
      statusMAYBE: false,
      statusWAITLISTED: false,
    },
  })

  const [fetchAttendees, { loading: attendeesLoading }] = useLazyQuery(ATTENDEES_QUERY)

  // Update form values when event data is available
  useEffect(() => {
    if (event) {
      const admission = Boolean(event.admission)
      formMethods.reset({
        includeAttendeeNote: true,
        includePaymentAmounts: admission,
        includePaymentStatus: admission,
        includeStatus: true,
        includeQuantity: true,
        statusGOING: true,
        statusNOTGOING: false,
        statusMAYBE: false,
        statusWAITLISTED: false,
      })
    }
  }, [event, formMethods])

  const generatePdf = async () => {
    setIsGenerating(true)

    try {
      const formValues = formMethods.getValues()

      if (!event) {
        toast.error(t('Event not found'))
        setIsGenerating(false)
        return
      }

      // Get selected statuses
      const selectedStatuses: EventAttendanceStatus[] = []
      if (formValues.statusGOING) selectedStatuses.push('GOING')
      if (formValues.statusNOTGOING) selectedStatuses.push('NOTGOING')
      if (formValues.statusMAYBE) selectedStatuses.push('MAYBE')
      if (formValues.statusWAITLISTED) selectedStatuses.push('WAITLISTED')

      if (selectedStatuses.length === 0) {
        toast.error(t('Please select at least one attendee status'))
        setIsGenerating(false)
        return
      }

      // Fetch attendees data for all selected statuses in a single call
      const { data: attendeesData } = await fetchAttendees({
        variables: {
          page: 1,
          filter: {
            eventId: event.id,
            statuses: selectedStatuses as EventAttendanceFilterStatus[],
          },
          sortBy: 'NAME',
        },
      })

      const allAttendees = attendeesData?.eventAttendeeListPage?.entries || []

      if (allAttendees.length === 0) {
        toast.error(t('No attendees found for the selected statuses'))
        setIsGenerating(false)
        return
      }

      // Map peopleQty to quantity for the PDF document
      const attendeesWithQuantity = allAttendees.map((attendee) => ({
        ...attendee,
        quantity: attendee.peopleQty || 1,
      }))

      const blob = await pdf(
        <EventAttendeesPdfDocument
          attendees={attendeesWithQuantity}
          event={event}
          includeAttendeeNote={formValues.includeAttendeeNote}
          includePaymentAmounts={formValues.includePaymentAmounts}
          includePaymentStatus={formValues.includePaymentStatus}
          includeQuantity={formValues.includeQuantity}
          includeStatus={formValues.includeStatus}
          t={t}
        />
      ).toBlob()

      // Auto-download the PDF
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url

      // Generate filename with event name and date
      const eventDate = dayjs(event.startDate).format('YYYY-MM-DD')
      // The slug has a code like -xbkyrj at the end, so we need to remove it
      const eventName = event.slug.replace(SLUG_SUFFIX_REGEX, '')
      link.download = `SF_${eventName}_${eventDate}.pdf`

      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)

      // Show success toast
      toast.success(t('PDF generated and downloaded successfully!'))
      setPdfGenerated(true)
    } catch (err) {
      console.error('Error generating PDF:', err)
      toast.error(t('Failed to generate PDF'))
    } finally {
      setIsGenerating(false)
    }
  }

  if (isInitialLoading) {
    return <SFPageLoading />
  }

  if (error) {
    return <ErrorAlert error={error} />
  }

  if (!event) {
    return (
      <div className="mx-auto w-full max-w-4xl px-4 py-8">
        <p className="text-base text-foreground">{t('Event not found')}</p>
      </div>
    )
  }

  const canExport = event.canEdit || event.canSeeAttendees

  if (!canExport) {
    return (
      <div className="mx-auto w-full max-w-4xl px-4 py-8">
        <Alert variant="destructive">
          <CircleAlertIcon />
          <AlertTitle>{t('Access Denied')}</AlertTitle>
          <AlertDescription>{t("You don't have permission to export attendees for this event.")}</AlertDescription>
        </Alert>
      </div>
    )
  }

  return (
    <div className="mx-auto w-full max-w-4xl px-4 py-4">
      <Form formMethods={formMethods}>
        <div className="flex w-full flex-col items-stretch gap-4">
          <SFBackPageHeader
            lowerText={event.name}
            onBackClick={() =>
              navigate({
                to: '/event/$eventIdOrSlug',
                params: { eventIdOrSlug: event.slug || event.id },
              } as never)
            }
            title={t('Event Attendees PDF Generation')}
          />

          <Card className="mt-2">
            <CardHeader className="border-b pb-4">
              <CardTitle>{t('PDF Generation Options')}</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              {event.admission ? (
                <>
                  <CheckBoxFormField
                    disabled={!event.admission}
                    label={t('Include Payment Amounts')}
                    name="includePaymentAmounts"
                  />
                  <CheckBoxFormField
                    disabled={!event.admission}
                    label={t('Include Payment Status')}
                    name="includePaymentStatus"
                  />
                </>
              ) : null}
              <CheckBoxFormField label={t('Include People Quantity')} name="includeQuantity" />
              <CheckBoxFormField label={t('Include Attendee Notes')} name="includeAttendeeNote" />
              <CheckBoxFormField label={t('Include Attendee Status')} name="includeStatus" />
            </CardContent>
          </Card>

          <Card className="mt-2">
            <CardHeader className="border-b pb-4">
              <CardTitle>{t('Attendee Status Selection')}</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              <CheckBoxFormField label={t('Going')} name="statusGOING" />
              <CheckBoxFormField label={t('Not Going')} name="statusNOTGOING" />
              <CheckBoxFormField label={t('Maybe')} name="statusMAYBE" />
              <CheckBoxFormField label={t('Waitlisted')} name="statusWAITLISTED" />
            </CardContent>
          </Card>

          <div className="flex w-full flex-row flex-wrap items-center justify-end gap-4">
            <Button
              className="min-w-[10rem]"
              disabled={isGenerating || attendeesLoading}
              onClick={generatePdf}
              size="lg"
              type="button"
              variant={pdfGenerated ? 'secondary' : 'default'}
            >
              {isGenerating || attendeesLoading ? (
                <>
                  <Spinner className="mr-2 size-4" />
                  {t('Generate PDF')}
                </>
              ) : (
                t('Generate PDF')
              )}
            </Button>
            {pdfGenerated ? (
              <Button
                onClick={() =>
                  navigate({
                    to: '/event/$eventIdOrSlug',
                    params: { eventIdOrSlug: event.slug || event.id },
                  } as never)
                }
                size="lg"
                type="button"
                variant="outline"
              >
                {t('Back')}
              </Button>
            ) : null}
          </div>
        </div>
      </Form>
    </div>
  )
}
